import type { RichText, RichTextInline, RichTextMark } from '../schemas/experience.js';
import {
  type ExperienceVariable,
  experienceSegmentsOf,
  experienceVariableToken,
} from './variables.js';

/**
 * The renderer of the Texte block (§9.2), PURE and shared.
 *
 * §9.2 puts one condition on the storage format: « le rendu est une fonction
 * pure, jouée à l'identique par l'aperçu de l'éditeur et par le lecteur, pour
 * qu'il n'existe pas deux orthographes du même texte ». This is that function,
 * and it lives in `shared` for exactly that reason - the editor's preview and
 * the reader import the same one.
 *
 * ## No HTML goes in, and everything that comes out is escaped
 *
 * The input is a TREE of known nodes, never a string of markup: there is no
 * path by which an operator's keystroke becomes an element. Every piece of text
 * that reaches the output goes through `escape` first, the five characters
 * included - `&`, `<`, `>`, `"` and `'` - so a paragraph reading
 * `<script>alert(1)</script>` renders as those very words, which is what the
 * person typed and what the reader must see.
 *
 * The marks and the colour become ELEMENTS and a data attribute, never a style
 * attribute: §9.4 says « aucun CSS libre, à aucun niveau », and a colour is a
 * TOKEN of the theme (H28). `data-color="accent"` is what the reader's
 * stylesheet paints from, so a change of theme repaints the text and the
 * contrast of §9.4 stays calculable.
 *
 * ## An unresolved variable is left AS IS
 *
 * A token whose `resolveVariable` answers nothing keeps its stored spelling in
 * the output, escaped. §8.6 is explicit: an unresolved variable is « refusée à
 * la publication de l'expérience, jamais rendue vide en silence ». Blanking it
 * here would hide from the reader the very defect the version save refuses.
 * With no resolver at all - the editor's preview - every token renders as
 * itself, which is what the inspector's chip stands for.
 */

const ESCAPES: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** The five characters, and the ampersand FIRST so the others are not double-escaped. */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (character) => ESCAPES[character] ?? character);
}

/** The element each mark becomes. Nested in this order, so the output is stable. */
const MARK_ELEMENT: Readonly<Record<RichTextMark, string>> = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
};

const MARK_ORDER: readonly RichTextMark[] = ['bold', 'italic', 'underline'];

export type RenderRichTextOptions = {
  /**
   * What a variable renders as at this moment (§8.6: « une variable se résout
   * au moment où le texte est servi »). `null` - or no resolver at all - leaves
   * the token as it is stored.
   */
  readonly resolveVariable?: (variable: ExperienceVariable) => string | null;
};

/** One text run: its variables resolved, then escaped, then wrapped in its marks. */
function renderRun(
  inline: Extract<RichTextInline, { type: 'text' }>,
  options: RenderRichTextOptions,
): string {
  const resolved = experienceSegmentsOf(inline.text)
    .map((segment) => {
      if (segment.kind === 'text') return segment.text;
      // The stored spelling when nothing answers - see the note at the top.
      return (
        options.resolveVariable?.(segment.variable) ?? experienceVariableToken(segment.variable)
      );
    })
    .join('');

  let html = escapeHtml(resolved);
  for (const mark of MARK_ORDER) {
    if (!inline.marks.includes(mark)) continue;
    const element = MARK_ELEMENT[mark];
    html = `<${element}>${html}</${element}>`;
  }
  if (inline.color !== null) html = `<span data-color="${inline.color}">${html}</span>`;
  return html;
}

function renderChildren(
  children: readonly RichTextInline[],
  options: RenderRichTextOptions,
): string {
  return children
    .map((inline) => (inline.type === 'break' ? '<br>' : renderRun(inline, options)))
    .join('');
}

/**
 * A rich text document as SAFE HTML - what the preview and the reader both
 * paint.
 *
 * An empty document renders the empty string rather than an empty paragraph: a
 * Texte block nobody has typed into must take no room.
 */
export function renderRichText(document: RichText, options: RenderRichTextOptions = {}): string {
  return document.nodes
    .map((node) => {
      if (node.type === 'paragraph') return `<p>${renderChildren(node.children, options)}</p>`;
      const items = node.items
        .map((item) => `<li>${renderChildren(item.children, options)}</li>`)
        .join('');
      return `<ul>${items}</ul>`;
    })
    .join('');
}
